import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
// import { MuiPickersUtilsProvider, KeyboardDatePicker, KeyboardTimePicker } from '@mui/lab';
import styled from "styled-components";
import { TimeSlotDto } from "../../../api-client";
import { Save } from "@mui/icons-material";
import dayjs from "dayjs";
import {
  useDeleteBlockedSlot,
  useSaveBlockedSlotForPatient,
} from "../../../stores/Appointment/appointment.mutation";
import MobiliDate from "../../MobiliDate";
import MobiliTime from "../../MobiliTime";
import { observer } from "mobx-react";

type LocalTimeSlot = TimeSlotDto & { _key: string };

interface PatientAvailabilityProps {
  patientId: number;
  blockedData: TimeSlotDto[];
  readOnly?: boolean;
  /** LLM-suggested blocked slots injected as unsaved draft rows */
  aiDraftBlockedSlots?: Array<{ id?: string | null; start: string; end: string }>;
  /** Called after AI draft blocked slots are saved or dismissed */
  onAiDraftClear?: () => void;
  /** Incrementing counter — each bump triggers an automatic save of the current draft */
  triggerSave?: number;
  /** Incrementing counter — each bump removes draft rows without saving */
  triggerReject?: number;
}

const StyledTable = styled(Table)`
  width: 100% !important;
  border-collapse: collapse !important;

  & th,
  & td {
    border: 1px solid #dcdcdc !important;
    padding: 7px !important;
    text-align: left !important;
  }
`;

const CustomInput = styled("div")`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const PatientAvailability: React.FC<PatientAvailabilityProps> = observer(
  ({
    patientId,
    blockedData,
    readOnly,
    aiDraftBlockedSlots,
    onAiDraftClear,
    triggerSave,
    triggerReject,
  }) => {
    const { mutate: saveBlockedSlotForPatient, isPending: isSaving } =
      useSaveBlockedSlotForPatient();
    const { mutate: deleteBlockedSlot } = useDeleteBlockedSlot();
    const [localBlockedData, setLocalBlockedData] = useState<LocalTimeSlot[]>([]);
    // Track which indices are AI drafts for highlighting
    const [aiDraftIndices, setAiDraftIndices] = useState<Set<number>>(new Set());
    // Track the last injected draft prop to prevent duplicate injection
    const lastInjectedDraftRef = useRef<typeof aiDraftBlockedSlots>(undefined);
    // Synced ref for aiDraftIndices so blockedData effect always reads latest value
    const aiDraftIndicesRef = useRef<Set<number>>(new Set());
    // Stable key counter for React reconciliation
    const keyCounterRef = useRef(0);
    const genKey = () => `rk-${++keyCounterRef.current}`;

    useEffect(() => {
      const serverData: LocalTimeSlot[] = (blockedData || [])
        .slice()
        .sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime())
        .map((s) => ({ ...s, _key: s.id || genKey() }));

      // If we have draft slots to inject, append them after server data
      if (
        aiDraftBlockedSlots &&
        aiDraftBlockedSlots.length > 0 &&
        aiDraftBlockedSlots !== lastInjectedDraftRef.current
      ) {
        lastInjectedDraftRef.current = aiDraftBlockedSlots;
        const newSlots: LocalTimeSlot[] = aiDraftBlockedSlots.map((slot) => ({
          start: slot.start,
          end: slot.end,
          type: "PatientUnavailable",
          _key: genKey(),
        }));
        const indices = new Set<number>();
        for (let i = 0; i < newSlots.length; i++) {
          indices.add(serverData.length + i);
        }
        setAiDraftIndices(indices);
        aiDraftIndicesRef.current = indices;
        setLocalBlockedData([...serverData, ...newSlots]);
        return;
      }

      // No drafts to inject — sync with server data, preserving existing draft rows
      const currentDraftIndices = aiDraftIndicesRef.current;
      setLocalBlockedData((prev) => {
        const draftRows = Array.from(currentDraftIndices)
          .sort((a, b) => a - b)
          .map((i) => prev[i])
          .filter(Boolean);

        if (draftRows.length > 0) {
          const newIndices = new Set<number>();
          for (let i = 0; i < draftRows.length; i++) {
            newIndices.add(serverData.length + i);
          }
          setAiDraftIndices(newIndices);
          aiDraftIndicesRef.current = newIndices;
          return [...serverData, ...draftRows];
        }

        setAiDraftIndices(new Set());
        aiDraftIndicesRef.current = new Set();
        return serverData;
      });
    }, [blockedData, aiDraftBlockedSlots]);

    const handleAddRow = () => {
      const today = dayjs();
      const startOfDay = today.startOf("day");
      const endOfDay = today.endOf("day");
      // console.log("startday", startOfDay, "& endday", endOfDay);

      const newRow: LocalTimeSlot = {
        start: startOfDay.toISOString(),
        end: endOfDay.toISOString(),
        type: "PatientUnavailable",
        _key: genKey(),
      };

      setLocalBlockedData((prevData) => [...prevData, newRow]);
    };

    const handleCellChange = (field: string, value: any, index: number) => {
      const updatedData = [...localBlockedData];
      const row = updatedData[index];

      if (field === "start.date") {
        if (typeof value === "string" && value) {
          const existing = dayjs(row.start);
          const newDate = dayjs(value);
          const newStart = newDate
            .hour(existing.hour())
            .minute(existing.minute())
            .second(existing.second())
            .millisecond(existing.millisecond());
          row.start = newStart.toISOString();
          setLocalBlockedData(updatedData);
        } else {
          console.error("Invalid date value");
        }
      } else if (field === "end.date") {
        if (typeof value === "string" && value) {
          const existing = dayjs(row.end);
          const newDate = dayjs(value);
          const newEnd = newDate
            .hour(existing.hour())
            .minute(existing.minute())
            .second(existing.second())
            .millisecond(existing.millisecond());
          row.end = newEnd.toISOString();
          setLocalBlockedData(updatedData);
        } else {
          console.error("Invalid date value");
        }
      } else if (field === "start.time" || field === "end.time") {
        // Handle time changes from TimeField (format: "hh:mm")
        if (typeof value === "string" && value.match(/^\d{2}:\d{2}$/)) {
          const [hours, minutes] = value.split(":").map(Number);

          if (field === "start.time") {
            const newStart = dayjs(row.start).hour(hours).minute(minutes);
            row.start = newStart.toISOString();
          } else if (field === "end.time") {
            const newEnd = dayjs(row.end).hour(hours).minute(minutes);
            row.end = newEnd.toISOString();
          }

          setLocalBlockedData(updatedData);
        } else {
          console.error("Invalid time format. Expected hh:mm");
        }
      }
    };

    const handleDelete = async (index: any) => {
      // console.log("Index", index, localBlockedData);
      const deletedRow = localBlockedData[index];

      // Check if the row has an ID (i.e., it has already been added and saved)
      if (deletedRow.id) {
        // Call your API to delete the row by its ID
        try {
          deleteBlockedSlot({ timeSlotId: deletedRow.id, patientId });
          // fetchData();
        } catch (error) {
          console.error("Error deleting row:", error);
          return;
        }
      }

      // If it's a new row or if it was successfully deleted, update the data
      const updatedData = [...localBlockedData];
      updatedData.splice(index, 1);
      setLocalBlockedData(updatedData);

      // Re-index AI draft indices after row removal
      if (aiDraftIndices.size > 0) {
        const newIndices = new Set<number>();
        for (const i of aiDraftIndices) {
          if (i < index) {
            newIndices.add(i);
          } else if (i > index) {
            newIndices.add(i - 1);
          }
          // i === index: deleted draft row — skip
        }
        setAiDraftIndices(newIndices);
        aiDraftIndicesRef.current = newIndices;
        // All drafts removed — notify parent so buttons/disabled state clear
        if (newIndices.size === 0) {
          onAiDraftClear?.();
        }
      }
    };

    const handleOk = async () => {
      try {
        const updatedData = localBlockedData.map((item) => {
          return {
            id: item.id ? item.id : undefined,
            start: dayjs(item.start).toISOString(),
            end: dayjs(item.end).toISOString(),
            type: "PatientUnavailable",
          };
        });

        // Check both ref and state — ref may have been cleared by a prior
        // failed triggerSave while state still tracks the draft rows.
        const hasDrafts = aiDraftIndicesRef.current.size > 0 || aiDraftIndices.size > 0;

        saveBlockedSlotForPatient(
          { patientId, blockedSlots: updatedData },
          {
            onSuccess: () => {
              // Clear AI draft state only after successful save
              if (hasDrafts) {
                setAiDraftIndices(new Set());
                aiDraftIndicesRef.current = new Set();
                onAiDraftClear?.();
              }
            },
            onError: (error) => {
              console.error("Save failed:", error);
            },
          },
        );

        // fetchData();
      } catch (error) {
        console.error("Error saving data:", error);
      }
    };

    // Auto-save when parent triggers approval
    useEffect(() => {
      if (triggerSave && triggerSave > 0 && aiDraftIndicesRef.current.size > 0) {
        // Build save payload from current localBlockedData (snapshot the data now)
        const dataToSave = localBlockedData.map((item) => ({
          id: item.id ? item.id : undefined,
          start: dayjs(item.start).toISOString(),
          end: dayjs(item.end).toISOString(),
          type: "PatientUnavailable",
        }));

        // Snapshot before clearing so we can restore on error
        const savedIndices = new Set(aiDraftIndicesRef.current);

        // Clear only the REF (not the state) — this prevents the useEffect from
        // re-appending draft rows when the mutation's query invalidation refetches
        // blockedData. The state is kept so draft highlighting stays until onSuccess.
        aiDraftIndicesRef.current = new Set();

        if (dataToSave.length > 0) {
          saveBlockedSlotForPatient(
            { patientId, blockedSlots: dataToSave },
            {
              onSuccess: () => {
                setAiDraftIndices(new Set());
                onAiDraftClear?.();
              },
              onError: (error) => {
                console.error("AI draft blocked slots save failed:", error);
                // Restore the ref so the next triggerSave / manual save can retry
                aiDraftIndicesRef.current = savedIndices;
              },
            },
          );
        } else {
          setAiDraftIndices(new Set());
          onAiDraftClear?.();
        }
      }
    }, [triggerSave]); // eslint-disable-line react-hooks/exhaustive-deps

    // Remove draft rows when parent triggers rejection
    useEffect(() => {
      if (triggerReject && triggerReject > 0 && aiDraftIndices.size > 0) {
        setLocalBlockedData((prev) => prev.filter((_, i) => !aiDraftIndices.has(i)));
        setAiDraftIndices(new Set());
        aiDraftIndicesRef.current = new Set();
        lastInjectedDraftRef.current = undefined;
        onAiDraftClear?.();
      }
    }, [triggerReject]); // eslint-disable-line react-hooks/exhaustive-deps

    // console.log("NewRowDtaa", blockedData);
    return (
      <Box>
        <Grid container>
          <Grid item xs={12}>
            <TableContainer sx={{ maxWidth: "100%" }}>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <th>Startdatum</th>
                    <th>Startzeit</th>
                    <th>Enddatum</th>
                    <th>Endzeit</th>
                    <th>Aktionen</th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {localBlockedData?.map((row, index) => (
                    <TableRow
                      key={row._key}
                      sx={
                        aiDraftIndices.has(index)
                          ? {
                              backgroundColor: "rgba(255, 215, 0, 0.22)",
                              borderLeft: "3px solid #f5a623",
                              animation: "aiHighlightPulse 2s ease-in-out 1",
                              "@keyframes aiHighlightPulse": {
                                "0%": { backgroundColor: "rgba(255, 215, 0, 0.45)" },
                                "100%": { backgroundColor: "rgba(255, 215, 0, 0.22)" },
                              },
                            }
                          : !row.id
                            ? {
                                backgroundColor: "rgba(255, 215, 0, 0.12)",
                              }
                            : undefined
                      }
                    >
                      <td>
                        <CustomInput>
                          <MobiliDate
                            value={row.start}
                            onChange={(dateValue: string) =>
                              handleCellChange("start.date", dateValue, index)
                            }
                            placeholder="tt.mm.jj"
                            yearFormat="2-digit"
                            size="small"
                            sx={{ width: 96 }}
                          />
                        </CustomInput>
                      </td>
                      <td>
                        <MobiliTime
                          value={
                            !row.id &&
                            !aiDraftIndices.has(index) &&
                            dayjs(row.start).hour() === 0 &&
                            dayjs(row.start).minute() === 0
                              ? ""
                              : dayjs(row.start).format("HH:mm")
                          }
                          onChange={(time: string) => handleCellChange("start.time", time, index)}
                          placeholder="hh:mm"
                          size="small"
                          sx={{ width: 82 }}
                        />
                      </td>
                      <td>
                        <CustomInput>
                          <MobiliDate
                            value={row.end}
                            onChange={(dateValue: string) =>
                              handleCellChange("end.date", dateValue, index)
                            }
                            placeholder="tt.mm.jj"
                            yearFormat="2-digit"
                            size="small"
                            sx={{ width: 96 }}
                          />
                        </CustomInput>
                      </td>
                      <td>
                        <MobiliTime
                          value={
                            !row.id &&
                            !aiDraftIndices.has(index) &&
                            dayjs(row.end).hour() === 23 &&
                            dayjs(row.end).minute() === 59
                              ? ""
                              : dayjs(row.end).format("HH:mm")
                          }
                          onChange={(time: string) => handleCellChange("end.time", time, index)}
                          placeholder="hh:mm"
                          size="small"
                          sx={{ width: 82 }}
                        />
                      </td>
                      <td>
                        {!readOnly && ( // Show the delete button if not in read-only mode
                          <DeleteIcon onClick={() => handleDelete(index)} />
                        )}
                      </td>
                    </TableRow>
                  ))}
                </TableBody>
              </StyledTable>
              {!readOnly && ( // Show the "Add Row" button if not in read-only mode
                <Box display="flex" justifyContent="flex-end" p={1}>
                  <Button
                    //   variant="contained"
                    //   color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddRow}
                  >
                    Neu
                  </Button>
                </Box>
              )}
            </TableContainer>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          mt={2}
          sx={{ border: "0px solid red" }}
          display="flex"
          justifyContent="flex-end"
        >
          {!readOnly && ( // Show the "Save" button if not in read-only mode
            <Button
              variant="contained"
              color="primary"
              startIcon={<Save />}
              onClick={handleOk}
              disabled={isSaving}
            >
              {isSaving ? "Speichern..." : "Speichern"}
            </Button>
          )}
        </Grid>
      </Box>
    );
  },
);

export default PatientAvailability;
